import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './css/App.css';
import { StockSearch } from './components/StockSearch';
import Login from './components/Login';
import Register from './components/Register';
import PrivateRoute from './components/PrivateRoute';
import DailyAnalysis from './components/DailyAnalysis';
import DailyHistory from './components/DailyHistory';
import StockInformation from './components/StockInformation';
import RecentGreenDots from './components/RecentGreenDots';
import RecentRSIOutliers from './components/RecentRSIOutliers';
import Terms from './components/Terms';
import { Navbar, Container, Button, Nav, Row, Col, Spinner, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import PrivacyPolicy from './components/PrivacyPolicy';
import config from './config';

function App() {
  const isAuthenticated = !!sessionStorage.getItem('token');
  const [symbol, setSymbol] = useState('');
  const [dailyAnalysis, setDailyAnalysis] = useState(null);
  const [dailyHistory, setDailyHistory] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const logout = () => {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('username');
    window.location.href = '/login';
  };

  const handleSearch = async (symbol) => {
    setLoading(true);
    setError('');
    try {
      const analysisResponse = await fetch(`${config.apiUrl}/api/v1/analysis/last-analysis/${symbol}`);
      const historyResponse = await fetch(`${config.apiUrl}/api/v1/data/history/${symbol}`);
      const infoResponse = await fetch(`${config.apiUrl}/api/v1/data/stock/${symbol}`);
      if (!analysisResponse.ok || !historyResponse.ok || !infoResponse.ok) {
        throw new Error('Failed to fetch data');
      }
      const analysisData = await analysisResponse.json();
      const historyData = await historyResponse.json();
      const infoData = await infoResponse.json();
      setDailyAnalysis(analysisData);
      setDailyHistory(historyData);
      setInfo(infoData);
      setSymbol(symbol);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Router>
      <div className="App">
        <Navbar bg="dark" variant="dark" expand="lg">
          <Container>
            <Navbar.Brand as={Link} to="./">Stock Analysis</Navbar.Brand>
            <Nav className="ml-auto">
              {isAuthenticated && (
                <StockSearch setSymbol={setSymbol} setDailyAnalysis={setDailyAnalysis} setDailyHistory={setDailyHistory} setInfo={setInfo} />
              )}
              {isAuthenticated ? (
                <Button variant="outline-light" onClick={logout}>
                  Logout
                </Button>
              ) : (
                <Button variant="outline-light" as={Link} to="/register">
                  Register
                </Button>
              )}
            </Nav>
          </Container>
        </Navbar>
        <Container className="mt-4">
          <Routes>
            <Route path="/" element={<PrivateRoute><div /></PrivateRoute>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/recent-green-dots" element={<RecentGreenDots handleSearch={handleSearch} />} />
            <Route path="/recent-rsi-outliers" element={<RecentGreenDots handleSearch={handleSearch} />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          </Routes>
          {isAuthenticated && !symbol && (
            <><Row className='mt-4'>
              <Col>
                <h2>Welcome to Stock Analysis</h2>
                <p>Stock Analysis is a tool that provides updated analysis information for
                   over 1800 stocks. The information is not financial advice and should not
                    be used to make financial decisions. Stock Analysis is not responsible for
                    any financial losses that may occur from using this tool. Please read our 
                    <Link to="/terms">Terms of Service</Link> and <Link to="/privacypolicy">Privacy Policy</Link> for more information.
                </p>
                <p>Search for a stock symbol to get started or click into look at
                  recent green dots or RSI outliers which can be used to find potential
                  stocks to buy or sell.
                </p>
                <p>For the S&P500, an additional Machine Learning algorithm is applied.
                  This algorithm predicts the next day's closing price based on the previous
                  10 days of closing prices. The algorithm is trained on the S&P500 and
                  is not guaranteed to be accurate. The algorithm is not financial advice
                  and should not be used to make financial decisions.
                </p>
              </Col>
            </Row><Row className="mt-4">
                <Col>
                  <RecentGreenDots handleSearch={handleSearch} />
                </Col>
                <Col>
                  <RecentRSIOutliers handleSearch={handleSearch} />
                </Col>
              </Row></>
          )}
          {isAuthenticated && loading && (
            <Row className="mt-4">
              <Col>
                <Spinner animation="border" role="status">
                  <span className="sr-only">Loading...</span>
                </Spinner>
              </Col>
            </Row>
          )}
          {isAuthenticated && error && (
            <Row className="mt-4">
              <Col>
                <Alert variant="danger">{error}</Alert>
              </Col>
            </Row>
          )}
          {isAuthenticated && info && (
            <Row className="mt-4">
              <Col>
                <StockInformation info={info} />
              </Col>
            </Row>
          )}
          {isAuthenticated && dailyAnalysis && (
            <Row className="mt-4">
              <Col>
                <DailyAnalysis dailyAnalysis={dailyAnalysis} />
              </Col>
            </Row>
          )}
          {isAuthenticated && dailyHistory && (
            <Row className="mt-4">
              <Col>
                <DailyHistory dailyHistory={dailyHistory} />
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </Router>
  );
}

export default App;